import type { GameMode } from './types';

/** Player colours indexed by `colorIndex` (one per seat, MAX_PLAYERS entries). */
export const PLAYER_COLORS = [
  '#e53935',
  '#1e88e5',
  '#43a047',
  '#fdd835',
  '#8e24aa',
  '#fb8c00',
  '#00acc1',
  '#d81b60',
  '#6d4c41',
  '#c0ca33',
  '#3949ab',
  '#eceff1',
] as const;

/**
 * Team colours indexed by `team` (1 = Blue, 2 = Red); 0 = no team. Both reuse a player colour.
 * Teams 3–4 are reserved for Green and Yellow, but the server only assigns 2 teams today.
 */
export const TEAM_COLORS: Readonly<Record<number, string>> = {
  1: PLAYER_COLORS[1],
  2: PLAYER_COLORS[0],
};

/** What players call each team: the colour, never "A" or "B". */
export const TEAM_NAMES: Readonly<Record<number, string>> = { 1: 'Blue', 2: 'Red' };

/** "Blue", "Red", or "Team 3" for a team without a name. */
export function teamName(team: number): string {
  return TEAM_NAMES[team] ?? `Team ${team}`;
}

/** The team's colour, or undefined for team 0 and unknown teams. */
export function teamColor(team: number): string | undefined {
  return TEAM_COLORS[team];
}

/**
 * Colour for everything that shows a player's colour: tint mask, ground ring, HP bar, name and
 * HUD swatches. In Teams mode a player on a team takes the team colour; otherwise (FFA, or a
 * lobby player before teams are assigned) the seat colour.
 */
export function tintFor(colorIndex: number, team: number, mode: GameMode): string {
  if (mode === 'teams' && team > 0) {
    const c = TEAM_COLORS[team];
    if (c) return c;
  }
  return PLAYER_COLORS[colorIndex] ?? PLAYER_COLORS[0];
}
